import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { ArrowLeft, BarChart3, Clock } from 'lucide-react';

interface Stats {
  total: { seconds: number; session_count: number };
  by_source: Array<{ source: string; seconds: number; count: number }>;
  by_weekday: Array<{ weekday: number; seconds: number }>;
  by_week: Array<{ week_start: string; seconds: number; sessions: number }>;
  by_subject: Array<{ subject: string; seconds: number; sessions: number }>;
  by_day: Array<{ day: string; seconds: number }>;
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const SOURCE_LABELS: Record<string, string> = { homework: 'Portal-HA', manual: 'Manuell', todo: 'TODOs' };
const SOURCE_COLORS: Record<string, string> = { homework: '#3b82f6', manual: '#6366f1', todo: '#22c55e' };

function Bars({
  data,
  label,
  height = 160,
  valueFormatter = formatDuration,
  color = '#3b82f6',
}: {
  data: Array<{ key: string; value: number; sub?: string }>;
  label: string;
  height?: number;
  valueFormatter?: (n: number) => string;
  color?: string;
}) {
  const max = Math.max(1, ...data.map(d => d.value));
  const barWidth = 100 / Math.max(1, data.length);
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-300 mb-2">{label}</h3>
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" className="w-full" style={{ height: `${height}px` }}>
        {data.map((d, i) => {
          const h = Math.max(1, (d.value / max) * (height - 24));
          return (
            <g key={i}>
              <rect
                x={i * barWidth + 0.5}
                y={height - 20 - h}
                width={barWidth - 1}
                height={h}
                fill={color}
                rx="0.5"
              />
              <title>{d.key}: {valueFormatter(d.value)}{d.sub ? ` · ${d.sub}` : ''}</title>
            </g>
          );
        })}
      </svg>
      <div className="flex text-[10px] text-gray-500 mt-1" style={{ fontSize: '0.625rem' }}>
        {data.map((d, i) => (
          <div key={i} style={{ width: `${barWidth}%` }} className="text-center truncate px-0.5">
            {d.key}
          </div>
        ))}
      </div>
    </div>
  );
}

const StatistikPage: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<Stats>('/api/statistics/homework').then(setStats).catch(err => setError(err?.message || 'Fehler'));
  }, []);

  const weekdayBars = useMemo(() => {
    if (!stats) return [];
    const map = new Map(stats.by_weekday.map(d => [d.weekday, d.seconds]));
    return [1, 2, 3, 4, 5, 6, 7].map(w => ({
      key: WEEKDAY_LABELS[w - 1],
      value: map.get(w) ?? 0,
    }));
  }, [stats]);

  const weekBars = useMemo(() => {
    if (!stats) return [];
    return stats.by_week.map(w => ({
      key: new Date(w.week_start).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
      value: w.seconds,
      sub: `${w.sessions} Sitzungen`,
    }));
  }, [stats]);

  const dayBars = useMemo(() => {
    if (!stats) return [];
    return stats.by_day.map(d => ({
      key: new Date(d.day).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
      value: d.seconds,
    }));
  }, [stats]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
        <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <BarChart3 className="w-6 h-6 text-cyan-400" />
        <h1 className="text-xl font-semibold">Hausaufgaben-Statistik</h1>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
        {error && <p className="text-red-400 text-sm">{error}</p>}
        {!stats && !error && <p className="text-gray-500 text-sm">Lade…</p>}

        {stats && stats.total.session_count === 0 && (
          <p className="text-gray-500 text-sm">
            Noch keine Sitzungen aufgezeichnet. Starte auf <Link to="/hausaufgaben" className="text-blue-400">Hausaufgaben</Link>,
            {' '}<Link to="/manuelle-hausaufgaben" className="text-blue-400">Manuelle HA</Link> oder
            {' '}<Link to="/todos" className="text-green-400">TODOs</Link> einen Timer, damit hier Zahlen auftauchen.
          </p>
        )}

        {stats && stats.total.session_count > 0 && (
          <>
            <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <div className="text-xs uppercase text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Gesamt</div>
                <div className="text-3xl font-bold text-white mt-1">{formatDuration(stats.total.seconds)}</div>
                <div className="text-xs text-gray-400 mt-1">{stats.total.session_count} Sitzungen</div>
              </div>
              {stats.by_source.map(s => (
                <div key={s.source} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                  <div className="text-xs uppercase flex items-center gap-1" style={{ color: SOURCE_COLORS[s.source] || '#9ca3af' }}>
                    {SOURCE_LABELS[s.source] || s.source}
                  </div>
                  <div className="text-2xl font-bold text-white mt-1">{formatDuration(s.seconds)}</div>
                  <div className="text-xs text-gray-400 mt-1">{s.count} Sitzungen</div>
                </div>
              ))}
            </section>

            <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <Bars data={weekdayBars} label="Nach Wochentag" color="#06b6d4" />
            </section>

            <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <Bars data={weekBars} label={`Letzte ${weekBars.length} Wochen`} color="#3b82f6" />
            </section>

            <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <Bars data={dayBars} label="Letzte 30 Tage" color="#6366f1" />
            </section>

            <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-300 mb-3">Nach Fach / Quelle</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-700">
                    <th className="py-2">Fach / Lehrkraft</th>
                    <th className="py-2 text-right">Zeit</th>
                    <th className="py-2 text-right">Sitzungen</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.by_subject.map(s => (
                    <tr key={s.subject} className="border-b border-gray-700/50">
                      <td className="py-2 text-white">{s.subject}</td>
                      <td className="py-2 text-right text-gray-300 font-mono">{formatDuration(s.seconds)}</td>
                      <td className="py-2 text-right text-gray-400">{s.sessions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default StatistikPage;
